<template>
  <TaskPage ref="taskPageRef" no-brand no-auto-back :can-continue="!isLoadingRef" @action="onNext" @back="onBack">
    <template #title>Upload my tree</template>

    <SyncSourceHeader v-if="sourceRef" :source="sourceRef"/>

    <div v-if="isLoadingRef" class="loading">
      <LoadingIndicator/>
      <div>Loading details...</div>
    </div>

    <template v-else>
      <div class="alert alert-primary alert-sm">
        Photos and documents will not be included.
        <button type="button" class="btn btn-inline btn-link" @click="noContentModalRef?.open()">Why not?</button>
      </div>
      <div class="stats">
        <div class="stat">
          <div class="icon-area">
            <div class="people-pair">
              <img src="@/assets/icons/person_gray_150.png" class="back"/>
              <img src="@/assets/icons/person_gray_150.png"/>
            </div>
          </div>
          <div class="count">{{ personCountRef.toLocaleString() }}</div>
          <div class="label">people</div>
        </div>
        <div class="stat">
          <div class="icon-area">
            <div class="country-wrap">
              <CountryCloud :countries="topCountriesRef"/>
            </div>
          </div>
          <div class="count">{{ countryCountRef.toLocaleString() }}</div>
          <div class="label">countries</div>
        </div>
        <div class="stat disabled">
          <div class="icon-area">
            <div class="photo-doc-pair">
              <img src="@/assets/icons/document.svg" class="doc"/>
              <img src="@/assets/icons/photo_sepia.svg" />
            </div>
          </div>
          <div class="count">{{ contentCountRef.toLocaleString() }}</div>
          <div class="label">photos &amp; documents</div>
        </div>
      </div>
    </template>

    <template #secondaryAction>
      <button type="button" class="btn btn-outline-primary" @click="showSyncOptions">Options</button>
    </template>

    <template #backText>Cancel</template>
    <template #actionText>Create online tree</template>

    <LivingPeopleModal ref="livingPeopleModalRef" :session-id="sessionId"/>
    <SyncOptionsModal ref="syncOptionsModalRef" :session-id="sessionId" @edit-living="showLivingPeople"/>

    <Modal ref="noContentModalRef" info>
      <template #title>Photos &amp; documents</template>
      <p>When you upload your genealogy database file directly, only the data in the file is included. It does not upload any photos, documents, or other external files.</p>
      <p>If you use the Sync app to upload your tree, it can upload your photos, documents, and external files automatically.</p>
      <button type="button" class="btn btn-inline btn-link" @click="onSyncAppHelp">Learn more about the Sync app</button>
      <template #okText>Got it</template>
    </Modal>
  </TaskPage>
</template>

<style lang="scss" scoped>
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  color: #888;
}

.alert {
  margin-top: 1rem;
}

.stats {
  margin-top: 2rem;
  margin-left: auto;
  margin-right: auto;
  max-width: 450px;
  min-height: 200px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}

.stat {
  display: grid;
  grid-template-rows: 80px min-content min-content;
  justify-items: center;
  align-items: start;
  text-align: center;

  .icon-area {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #aaa;
  }

  .count {
    margin-top: 0.5rem;
    font-size: 1.5rem;
  }

  .label {
    font-size: 1rem;
    line-height: 1.3;
  }

  &.disabled {
    .count,
    .label {
      color: #ccc;
    }
  }
}

.people-pair {
  position: relative;

  img {
    position: absolute;
    width: 45px;
    transform: translate(-12px, -18px);
    filter: brightness(2);
  }

  .back {
    transform: translate(-32px, -28px);
    filter: brightness(3);
  }
}

.country-wrap {
  position: relative;
  width: 100px;
  height: 100%;

  .country-cloud {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.65);
  }
}

.photo-doc-pair {
  position: relative;
  width: 100px;
  height: 100%;
  opacity: 0.25;

  img {
    position: absolute;
    top: 60%;
    left: 40%;
    height: 45px;
    transform: translate(-50%, -50%) rotate(-10deg);
    filter: saturate(0);
  }

  .doc {
    top: 40%;
    left: 60%;
    transform: translate(-50%, -50%) rotate(5deg);
  }
}

.secondary-action {
  button {
    min-width: 100px;
  }
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { usePageTitle, unexpectedErrorEvent, hideModalAsync } from '@webapp/util/AppUtil'
import { useTelemetry } from '@webapp/util/Telemetry'
import { useSyncSessionStore } from './SyncSessionStore'
import { SyncScope, toSyncSource } from './SyncSessionModel'
import { ItemType, ValueCount } from '@/rd/ResearchDataModel'
import { CountryCodes } from '@/util/GeoUtil'
import TaskPage from '@webapp/util/TaskPage.vue'
import LoadingIndicator from '@webapp/util/LoadingIndicator.vue'
import CountryCloud from '@/explore/places/CountryCloud.vue'
import Modal from '@webapp/util/Modal.vue'
import SyncSourceHeader from './SyncSourceHeader.vue'
import LivingPeopleModal from './LivingPeopleModal.vue'
import SyncOptionsModal from './SyncOptionsModal.vue'

const props = defineProps<{
  sessionId: string
}>()

const emit = defineEmits(['started', 'back', 'unusable'])

const router = useRouter()
const syncSessionStore = useSyncSessionStore()
const { trackException } = useTelemetry()

const isLoadingRef = ref(true)
const livingPeopleModalRef = ref<InstanceType<typeof LivingPeopleModal>>()
const syncOptionsModalRef = ref<InstanceType<typeof SyncOptionsModal>>()
const taskPageRef = ref<InstanceType<typeof TaskPage>>()
const noContentModalRef = ref<InstanceType<typeof Modal>>()
const sessionRef = computed(() => syncSessionStore.getSession(props.sessionId))
const sourceRef = computed(() => sessionRef.value ? toSyncSource(sessionRef.value) : undefined)

const sourceDetailsRef = computed(() => syncSessionStore.getState(props.sessionId)?.sourceDetails)
const personCountRef = computed(() => sourceDetailsRef.value?.itemCounts[ItemType.Person]?.total ?? 0)
const contentCountRef = computed(() => sourceDetailsRef.value?.itemCounts[ItemType.Content]?.total ?? 0)
const topCountriesRef = computed<ValueCount[]>(() =>
  Object.entries(sourceDetailsRef.value?.birthCountryCounts ?? {})
    .map(([name, count]) => ({ value: CountryCodes.fromName(name), count }))
    .filter((c): c is ValueCount => !!c.value)
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value)),
)
const countryCountRef = computed(() => topCountriesRef.value.length)

function showLivingPeople() {
  livingPeopleModalRef.value?.open()
}

function showSyncOptions() {
  syncOptionsModalRef.value?.open()
}

async function onSyncAppHelp() {
  await hideModalAsync(noContentModalRef.value?.$el)
  router.push('/learn/sync')
}

usePageTitle('Upload my tree')

onMounted(async () => {
  // Returning here after a failed sync: the source data and the user's choices are still
  // loaded, so keep them rather than reloading and resetting the scope to defaults.
  if (syncSessionStore.getState(props.sessionId)?.sourceDetails) {
    isLoadingRef.value = false
    return
  }
  try {
    await syncSessionStore.refreshSessionAsync(props.sessionId)
    await syncSessionStore.loadSourceDetailsAsync(props.sessionId, new SyncScope())
    await syncSessionStore.loadLivingWindowGraphAsync(props.sessionId)
    isLoadingRef.value = false
  }
  catch (error) {
    // The server cleans up an idle session after a while -- resuming a cleaned-up one fails. Hand
    // back to the wizard so it can forget the session and let the user re-upload.
    trackException(error)
    emit('unusable')
  }
})

function onBack() {
  emit('back')
}

function startOrResumeSyncAsync() {
  if (syncSessionStore.getSession(props.sessionId)?.workspaceId) {
    return syncSessionStore.retrySyncAsync(props.sessionId)
  }
  return syncSessionStore.startSyncAsync(props.sessionId)
}

async function onNext() {
  try {
    await startOrResumeSyncAsync()
    emit('started')
  }
  catch (error) {
    taskPageRef.value?.setBusy(false)
    unexpectedErrorEvent.trigger({ error })
  }
}
</script>
